/* Fairsprout cart drawer (v13). Slide-out cart, opened on Add to cart and by the header cart icon. No libraries.
   - Add to cart: catches the submit of the main product form (data-type="add-to-cart-form") in the BUBBLE phase.
     The Printify personalization guard in theme.js runs in the capture phase and stops a submit that is not
     personalized yet (or has no photo-rights tick), so such an add never reaches this file.
   - Sends the form's own FormData (id, quantity, properties incl. Personalization ID) to /cart/add.js together with
     sections=cart-drawer, swaps the drawer HTML and opens it. If the request fails, the form is posted normally (/cart).
   - Lines change by line key (/cart/change.js). Drawer picks add by variant id (single-variant items only).
   - Loaded only when sections/cart-drawer.liquid is on the page (layout/theme.liquid).
   - QC fix 2026-10-27 (no layout shift inside the drawer): screen readers hear a visually hidden live region
     ([data-cart-drawer-live], emptied and refilled). The visible line ([data-cart-drawer-status]) has a fixed height:
     it is filled BEFORE the drawer opens after an add, only changes in place while the drawer is open (never emptied
     or collapsed) and is hidden again after the drawer has closed. Opened from the cart icon = no visible line.
     Re-renders keep the list's scroll position on + / - / Remove and drawer picks. */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer || !window.fetch || !window.FormData || !window.DOMParser) return;

  var SECTION = 'cart-drawer';
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var html = document.documentElement;
  var live = drawer.querySelector('[data-cart-drawer-live]');
  var status = drawer.querySelector('[data-cart-drawer-status]');
  var statusTitle = status ? status.querySelector('[data-status-title]') : null;
  var statusName = status ? status.querySelector('[data-status-name]') : null;
  var strings = {};
  try { strings = JSON.parse(drawer.querySelector('[data-cart-drawer-strings]').textContent); } catch (e) {}
  var reduceMotion = false;
  try { reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
  var lastTrigger = null;
  var busy = false;
  var closeTimer = null;

  function isOpen() { return drawer.hasAttribute('open'); }

  function shortTitle(title) {
    return String(title || '').trim().split(' | ')[0].split(', ')[0].trim();
  }

  // Screen readers only (visually hidden, so emptying it moves nothing). A short pause so the same words are read again.
  function announce(text) {
    if (!live) return;
    live.textContent = '';
    window.setTimeout(function () { live.textContent = text || ''; }, 60);
  }

  // Visible line: fixed height, so changing its words never moves the list below it.
  function setStatus(title, name, isError) {
    if (!status || !statusTitle || !statusName) return;
    statusTitle.textContent = title || '';
    statusName.textContent = name || '';
    status.classList.toggle('is-error', !!isError);
    status.hidden = false;
  }

  // While the drawer is open: change the line only when it is already shown (showing it now would push the list down).
  // Errors are shown anyway: the shopper must see them.
  function updateStatus(title, name, isError) {
    if (!status) return;
    if (status.hidden && !isError) return;
    setStatus(title, name, isError);
  }

  function clearStatus() {
    if (!status) return;
    status.hidden = true;
    status.classList.remove('is-error');
    if (statusTitle) statusTitle.textContent = '';
    if (statusName) statusName.textContent = '';
  }

  function updateCount(source) {
    var count = parseInt(source.getAttribute('data-count'), 10) || 0;
    var label = source.getAttribute('data-count-label');
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
    if (label) {
      document.querySelectorAll('[data-cart-label]').forEach(function (el) { el.textContent = label; });
    }
    var drawerCount = drawer.querySelector('[data-cart-drawer-count]');
    if (drawerCount) drawerCount.textContent = '(' + count + ')';
  }

  function focusables() {
    return Array.prototype.slice.call(drawer.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), [tabindex="0"]'))
      .filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
  }

  function focusTitle() {
    var title = drawer.querySelector('#CartDrawerTitle');
    if (title) {
      try { title.focus({ preventScroll: true }); } catch (e) { title.focus(); }
    }
  }

  // Swap the re-rendered drawer content. Focus stays on the same control (data-focus-key) when it still exists.
  // keepScroll: + / - / Remove and drawer picks keep the list where the shopper left it (new adds start at the top).
  function render(sectionHtml, keepScroll) {
    if (!sectionHtml) return false;
    var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    var fresh = doc.querySelector('[data-cart-drawer-inner]');
    var current = drawer.querySelector('[data-cart-drawer-inner]');
    if (!fresh || !current) return false;
    var active = document.activeElement;
    var key = active && drawer.contains(active) ? active.getAttribute('data-focus-key') : null;
    var hadFocusInside = active && current.contains(active);
    var oldBody = current.querySelector('.cart-drawer__body');
    var scrollTop = keepScroll && oldBody ? oldBody.scrollTop : 0;
    current.innerHTML = fresh.innerHTML;
    if (scrollTop) {
      var newBody = current.querySelector('.cart-drawer__body');
      if (newBody) newBody.scrollTop = scrollTop;
    }
    wakeImages();
    current.setAttribute('data-count', fresh.getAttribute('data-count'));
    current.setAttribute('data-count-label', fresh.getAttribute('data-count-label'));
    updateCount(fresh);
    if (key) {
      var same = drawer.querySelector('[data-focus-key="' + key + '"]');
      if (same) { same.focus(); return true; }
    }
    if (hadFocusInside && isOpen()) focusTitle();
    return true;
  }

  function fetchSection() {
    return fetch(root + '?sections=' + SECTION, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('section'); return r.json(); })
      .then(function (data) { return data[SECTION]; });
  }

  function refresh() {
    return fetchSection().then(render).catch(function () {});
  }

  // Drawer photos use loading="lazy" (no cost on page load). Before the drawer opens, start them at once.
  function wakeImages() {
    drawer.querySelectorAll('img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
  }

  function open(trigger) {
    if (closeTimer) { window.clearTimeout(closeTimer); closeTimer = null; }
    wakeImages();
    if (!isOpen()) {
      lastTrigger = trigger || document.activeElement;
      if (typeof drawer.showModal === 'function') {
        try { drawer.showModal(); } catch (e) { drawer.setAttribute('open', ''); }
      } else {
        drawer.setAttribute('open', '');
      }
      html.classList.add('drawer-open');
      void drawer.offsetWidth; // start the slide from the closed position
    }
    drawer.classList.add('is-open');
    focusTitle();
  }

  function close() {
    if (!isOpen() || closeTimer) return;
    drawer.classList.remove('is-open');
    var finish = function () {
      closeTimer = null;
      if (typeof drawer.close === 'function') {
        try { drawer.close(); } catch (e) { drawer.removeAttribute('open'); }
      } else {
        drawer.removeAttribute('open');
      }
      html.classList.remove('drawer-open');
      clearStatus();
      if (live) live.textContent = '';
      if (lastTrigger && lastTrigger.focus && document.body.contains(lastTrigger)) {
        try { lastTrigger.focus({ preventScroll: true }); } catch (e) { lastTrigger.focus(); }
      }
    };
    if (reduceMotion) { finish(); } else { closeTimer = window.setTimeout(finish, 230); }
  }

  function setLoading(button, on) {
    if (!button) return;
    button.classList.toggle('is-loading', on);
    if (on) { button.setAttribute('aria-busy', 'true'); } else { button.removeAttribute('aria-busy'); }
  }

  function readJson(res) {
    return res.json().then(function (data) { return { ok: res.ok, status: res.status, data: data }; },
      function () { return { ok: res.ok, status: res.status, data: null }; });
  }

  function errorText(data) {
    return (data && (data.description || data.message)) || strings.error || '';
  }

  // Add-to-cart error (sold out, quantity limit): shown under the main button; the drawer stays closed.
  function showAddError(form, submitter, text) {
    var wrap = form.closest('[data-main-buy]') || form;
    var box = wrap.querySelector('[data-add-error]');
    if (!box) return;
    box.textContent = text;
    if (submitter && !wrap.contains(submitter)) {
      try { wrap.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' }); } catch (e) { wrap.scrollIntoView(); }
    }
  }

  function clearAddError(form) {
    var wrap = form.closest('[data-main-buy]') || form;
    var box = wrap.querySelector('[data-add-error]');
    if (box) box.textContent = '';
  }

  function addedName(data) {
    var title = '';
    if (data && data.key) {
      var line = drawer.querySelector('[data-line-key="' + data.key + '"] [data-line-title]');
      if (line) title = line.textContent.trim();
    }
    if (!title && data) title = data.product_title || (data.items && data.items[0] && data.items[0].product_title) || '';
    return shortTitle(title);
  }

  function addedText(name) {
    return (strings.added || '[title]').replace('[title]', name);
  }

  // Main product form (bubble phase: runs after the personalization guard and after any handler on the form itself).
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (e.defaultPrevented || !form || !form.matches || !form.matches('form[data-type="add-to-cart-form"]')) return;
    e.preventDefault();
    if (busy) return;
    var submitter = e.submitter || form.querySelector('[type="submit"]');
    var body = new FormData(form);
    body.append('sections', SECTION);
    body.append('sections_url', window.location.pathname);
    busy = true;
    var added = false;
    setLoading(submitter, true);
    clearAddError(form);
    fetch(root + 'cart/add.js', {
      method: 'POST',
      body: body,
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then(readJson)
      .then(function (res) {
        if (res.ok) {
          added = true;
          var sectionHtml = res.data && res.data.sections && res.data.sections[SECTION];
          return (sectionHtml ? Promise.resolve(sectionHtml) : fetchSection()).then(function (h) {
            if (!render(h)) throw new Error('render');
            var name = addedName(res.data);
            // The drawer is still closed here, so the line is part of the first frame: nothing moves after it opens.
            if (isOpen()) { updateStatus(strings.added_title, name); } else { setStatus(strings.added_title, name); }
            open(submitter);
            announce(addedText(name));
            document.dispatchEvent(new CustomEvent('cart:added', { detail: res.data }));
          });
        }
        if (res.status >= 400 && res.status < 500 && res.data) {
          showAddError(form, submitter, errorText(res.data));
          return null;
        }
        throw new Error('add');
      })
      .catch(function () {
        // Added but the drawer could not be shown: go to the cart page. Not added: normal form post to /cart.
        if (added) { window.location.href = root + 'cart'; return; }
        HTMLFormElement.prototype.submit.call(form);
      })
      .then(function () {
        busy = false;
        setLoading(submitter, false);
      });
  });

  function post(url, payload, control) {
    if (busy) return;
    busy = true;
    var item = control && control.closest('.cart-drawer__item, .pick');
    if (item) item.classList.add('is-busy');
    setLoading(control, true);
    payload.sections = SECTION;
    payload.sections_url = window.location.pathname;
    return fetch(root + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'same-origin'
    })
      .then(readJson)
      .then(function (res) {
        if (!res.ok) {
          var message = errorText(res.data);
          updateStatus(message, '', true);
          announce(message);
          return null;
        }
        var sectionHtml = res.data && res.data.sections && res.data.sections[SECTION];
        return (sectionHtml ? Promise.resolve(sectionHtml) : fetchSection()).then(function (h) {
          render(h, true);
          return res.data;
        });
      })
      .catch(function () { updateStatus(strings.error, '', true); announce(strings.error); return null; })
      .then(function (data) {
        busy = false;
        if (item && document.body.contains(item)) item.classList.remove('is-busy');
        if (control && document.body.contains(control)) setLoading(control, false);
        return data;
      });
  }

  drawer.addEventListener('click', function (e) {
    if (e.target === drawer) { close(); return; } // tap on the dimmed page
    var target = e.target.closest ? e.target : e.target.parentElement;
    if (!target) return;
    if (target.closest('[data-cart-drawer-close]')) { e.preventDefault(); close(); return; }
    var change = target.closest('[data-qty-change]');
    if (change) {
      var qty = parseInt(change.getAttribute('data-qty'), 10);
      if (isNaN(qty)) qty = 0;
      var row = change.closest('.cart-drawer__item');
      var rowTitle = row ? row.querySelector('[data-line-title]') : null;
      var rowName = rowTitle ? shortTitle(rowTitle.textContent) : '';
      var promise = post('cart/change.js', { id: change.getAttribute('data-key'), quantity: qty }, change);
      if (promise) {
        promise.then(function (data) {
          if (!data) return;
          updateStatus(qty === 0 ? strings.removed_title : strings.updated_title, rowName);
          announce(qty === 0 ? strings.removed : strings.updated);
          document.dispatchEvent(new CustomEvent('cart:changed', { detail: data }));
        });
      }
      return;
    }
    var add = target.closest('[data-drawer-add]');
    if (add) {
      var title = add.getAttribute('data-title') || '';
      var p = post('cart/add.js', { items: [{ id: Number(add.getAttribute('data-drawer-add')), quantity: 1 }] }, add);
      if (p) {
        p.then(function (data) {
          if (!data) return;
          var pickName = shortTitle(title);
          updateStatus(strings.added_title, pickName);
          announce(addedText(pickName));
          focusTitle();
          document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
        });
      }
    }
  });

  drawer.addEventListener('cancel', function (e) { e.preventDefault(); close(); });
  drawer.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    var items = focusables();
    if (!items.length) { e.preventDefault(); return; }
    var first = items[0];
    var last = items[items.length - 1];
    var title = drawer.querySelector('#CartDrawerTitle');
    if (e.shiftKey && (document.activeElement === first || document.activeElement === title)) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  });

  // Header cart icon opens the drawer (it stays a link to /cart without JavaScript, or with a modifier key).
  document.querySelectorAll('[data-cart-link]').forEach(function (link) {
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('aria-controls', 'CartDrawer');
    // Start loading the drawer photos as soon as the shopper reaches for the icon.
    ['pointerenter', 'touchstart', 'focus'].forEach(function (type) {
      link.addEventListener(type, wakeImages, { once: true, passive: true });
    });
  });
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-cart-link]');
    if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    open(link);
  });

  // Quick adds from the product page "Pairs well with" cards (cross-sell.js): keep the drawer up to date.
  document.addEventListener('cart:quick-added', function () { refresh(); });
  // Back/forward cache: the cart may have changed on another page.
  window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });
})();
